import { ReactComponent as CloseIcon } from '@assets/icons/close.svg';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';

import {
  Box,
  Button,
  Flex,
  Stack,
  Text,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { convertDuration, formatCurrency } from 'utils';

import { ProviderInterface, ServiceInterface } from 'types';

import { AppointmentAddonItem, ProviderItem } from './components';

interface AppointmentItemProps {
  service: ServiceInterface;
  provider: ProviderInterface | null;
  isMoveAppt: boolean;
  onRemoveService: () => void;
}

const AppointmentDetailsItem = ({
  service,
  provider,
  isMoveAppt,
  onRemoveService
}: AppointmentItemProps) => {
  const showSvcPrice = useShowSvcPrice();
  const { selectedAddons } = useAppSelector(
    (state) => state.bookingAppointment
  );
  const {
    colors: {
      palette: {
        booking: {
          stepTime: {
            apptDetailsList: { detailsItem }
          }
        }
      }
    }
  } = useTheme();

  const iconSize = useBreakpointValue({ base: 18, md: 24 }, { fallback: 'md' });

  const itemPrice = service.svc_price || service.retail;
  const serviceAddons = selectedAddons[service.id];
  const isAddonsExist = serviceAddons?.length > 0;

  return (
    <Stack gap='5px'>
      <Flex
        align='center'
        bg={detailsItem.background}
        borderRadius='6px'
        py='20px'
        pl={{ base: 0, md: '5px' }}
        pr={{ base: '17px', md: '20px', lg: '30px', xl: '40px' }}
        justifyContent='space-between'
        alignItems='center'
        gap={{ base: '0', md: '7px' }}
      >
        <Flex
          justifyContent={{ base: 'center', md: 'space-between' }}
          gap={{ base: '10px', md: '20px' }}
          w='100%'
          flexDirection='row'
        >
          <Flex w={{ base: '100%', md: '45%' }} gap='0' alignItems='center'>
            <Button
              display={isMoveAppt ? 'none' : 'block'}
              w='40px'
              h='40px'
              ml='15px'
              mt='1px'
              p={0}
              variant='text'
              flexShrink={0}
              onClick={onRemoveService}
            >
              <CloseIcon width={iconSize} height={iconSize} />
            </Button>

            <Stack flex='1 1 0 ' gap={0} ml={isMoveAppt ? '20px' : 0}>
              <Flex align='flex-start'>
                <Text
                  display='inline'
                  as='span'
                  color={detailsItem.serviceNameTextColor}
                  fontSize={{ base: '18px', md: '18px', xl: '25px' }}
                >
                  {service?.name}
                </Text>
                <Stack
                  alignItems='flex-end'
                  justifyContent='center'
                  gap='6px'
                  minW='90px'
                  ml='auto'
                  display={{ base: 'flex', md: 'none' }}
                >
                  {showSvcPrice && (
                    <Text
                      color={detailsItem.itemPriceTextColor}
                      fontSize={{
                        base: '16px',
                        sm: '18px',
                        md: '20px',
                        xl: '25px'
                      }}
                      lineHeight='125%'
                    >
                      {formatCurrency(itemPrice)}
                    </Text>
                  )}
                  <Text
                    color={detailsItem.itemDurationTextColor}
                    fontSize='15px'
                    whiteSpace='no-wrap'
                  >
                    {service.duration
                      ? convertDuration(service.duration)
                      : 'No specified'}
                  </Text>
                </Stack>
              </Flex>

              <Box
                display={{ base: 'block', md: 'none' }}
                w={{ base: '100%', md: '50%' }}
              >
                <ProviderItem provider={provider} />
              </Box>
            </Stack>
          </Flex>
          <Box
            display={{ base: 'none', md: 'flex' }}
            w={{ base: '100%', md: '55%' }}
          >
            <ProviderItem provider={provider} />
            <Stack
              display={{ base: 'none', md: 'flex' }}
              alignItems='flex-end'
              gap='6px'
              ml='20px'
              minW='90px'
            >
              {showSvcPrice && (
                <Text
                  color={detailsItem.itemPriceTextColor}
                  fontSize={{
                    base: '16px',
                    sm: '18px',
                    md: '20px',
                    xl: '25px'
                  }}
                  lineHeight='125%'
                >
                  {formatCurrency(itemPrice)}
                </Text>
              )}
              <Text
                color={detailsItem.itemDurationTextColor}
                fontSize='15px'
                whiteSpace='no-wrap'
              >
                {service.duration
                  ? convertDuration(service.duration)
                  : 'No specified'}
              </Text>
            </Stack>
          </Box>
        </Flex>
      </Flex>

      {isAddonsExist &&
        serviceAddons.map((item) => (
          <AppointmentAddonItem key={item.id} addon={item} />
        ))}
    </Stack>
  );
};

export default AppointmentDetailsItem;
